export interface LoginPolicy {
    allowPassword: boolean;
    showGoogle: boolean;
    forceGoogle: boolean;
    allowEmailCode: boolean;
}

export const PASSWORD_ONLY_POLICY: LoginPolicy = {
    allowPassword: true,
    showGoogle: false,
    forceGoogle: false,
    allowEmailCode: true,
};

/**
 * Is this the live site, where staff accounts must sign in with Google?
 *
 * Exact hosts only. Matching `*.archipro.co.nz` by suffix caught `dev.`, every
 * team server and every branch preview, so a staff address was shown Google and
 * nothing else on deploys whose OAuth allowlist does not carry their hostname —
 * and whose server never asks for SSO, since `security.forceGoogleForAdmin` is
 * true in the live config partition alone. A client stricter than the backend it
 * is talking to is a lockout, not a safeguard.
 *
 * `www` is absent on purpose: it redirects to the apex before any of this runs.
 */
function isProdHost(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }

    const host = window.location.hostname;

    return (
        host === 'archipro.co.nz' ||
        host === 'archipro.com.au' ||
        host === 'archiproamerica.com'
    );
}

export function getLoginPolicy(email: string): LoginPolicy {
    const normalizedEmail = email.trim().toLowerCase();
    const archiproDomain = '@archipro.co.nz';
    const isArchiproEmail = normalizedEmail.endsWith(archiproDomain);
    const isArchiproPlusEmail =
        isArchiproEmail &&
        normalizedEmail.slice(0, -archiproDomain.length).includes('+');

    if (isArchiproPlusEmail) {
        return PASSWORD_ONLY_POLICY;
    }

    if (!normalizedEmail.includes('@')) {
        return { ...PASSWORD_ONLY_POLICY, allowEmailCode: false };
    }

    if (isProdHost() && isArchiproEmail) {
        return {
            allowPassword: false,
            showGoogle: true,
            forceGoogle: true,
            allowEmailCode: false,
        };
    }

    return PASSWORD_ONLY_POLICY;
}

export function canShowEmailCodeLogin(options: {
    emailCodeLoginEnabled: boolean;
    flowIsLogin: boolean;
    email: string;
    publicUserType?: 'new' | 'existing';
    emailCodeLoginAvailable?: boolean;
}): boolean {
    if (!options.emailCodeLoginEnabled || !options.flowIsLogin) {
        return false;
    }

    if (options.publicUserType !== 'existing') {
        return false;
    }

    if (options.emailCodeLoginAvailable === false) {
        return false;
    }

    const policy = getLoginPolicy(options.email);

    return policy.allowEmailCode && !policy.forceGoogle;
}
